// ── Tipos ──────────────────────────────────────────────────────────────────

export interface ParsedRow {
  _raw: Record<string, string>;
  _errors: string[];
  _rowIndex: number;
  id: string;
  nome: string;
  carreira: string;
  sexo: string;
  nivel_atual: number;
  novo_nivel_2025: number;
  vencimento: number;
  avaliacao: string;
  ultima_progressao: string | null;
  data_entrada_funcao_publica: string | null;
  unidade_organica: string;
  subunidade_organica: string;
  pontos_atuais: number;
  email: string | null;
  telefone: string | null;
  data_nascimento: string | null;
  cpf: string | null;
  endereco: string | null;
  status: string | null;
  superior_hierarquico: string | null;
  categoria: string | null;
  despacho_autorizacao: string | null;
  certificados: string | null;
  excecao_prepav: boolean;
  descricao_subida: string | null;
}

// Mapeamento de campos da BD → aliases aceites no CSV
export const COLUMN_MAP: Record<string, string[]> = {
  id:                          ['id', 'nº', 'numero', 'num', 'numero_funcionario', 'nº_funcionario', 'n_funcionario', 'n_func', 'nfuncionario', 'funcionario_id', 'cod', 'codigo', 'code', 'matricula', 'ref', 'referencia'],
  nome:                        ['nome', 'name', 'nome_completo', 'full_name', 'nome_funcionario', 'funcionario', 'colaborador', 'trabalhador', 'employee'],
  carreira:                    ['carreira', 'career', 'categoria_carreira'],
  sexo:                        ['sexo', 'genero', 'gender', 'sex', 'género'],
  nivel_atual:                 ['nivel_atual', 'nivel', 'level', 'nível', 'nível_atual'],
  novo_nivel_2025:             ['novo_nivel_2025', 'novo_nivel', 'nivel_2025', 'new_level'],
  vencimento:                  ['vencimento', 'salario', 'salary', 'remuneracao', 'remuneração'],
  avaliacao:                   ['avaliacao', 'avaliação', 'evaluation', 'classificacao', 'classificação'],
  ultima_progressao:           ['ultima_progressao', 'ultima_progressão', 'last_progression', 'data_progressao'],
  data_entrada_funcao_publica: ['data_entrada_funcao_publica', 'data_entrada', 'entrada_fp', 'data_fp', 'entrada_funcao_publica'],
  unidade_organica:            ['unidade_organica', 'unidade', 'departamento', 'department', 'unidade_orgânica'],
  subunidade_organica:         ['subunidade_organica', 'subunidade', 'subdepartamento', 'subunidade_orgânica'],
  pontos_atuais:               ['pontos_atuais', 'pontos', 'points', 'pts'],
  email:                       ['email', 'e-mail', 'mail', 'correio'],
  telefone:                    ['telefone', 'telemovel', 'telemóvel', 'phone', 'tel', 'mobile'],
  data_nascimento:             ['data_nascimento', 'nascimento', 'birth_date', 'data_nasc'],
  cpf:                         ['cpf', 'nif', 'bi', 'cc', 'cartao_cidadao'],
  endereco:                    ['endereco', 'endereço', 'morada', 'address'],
  status:                      ['status', 'estado', 'situacao', 'situação', 'state'],
  superior_hierarquico:        ['superior_hierarquico', 'superior', 'chefe', 'responsavel', 'responsável'],
  categoria:                   ['categoria', 'category', 'cargo'],
  despacho_autorizacao:        ['despacho_autorizacao', 'despacho', 'dispatch', 'autorização'],
  certificados:                ['certificados', 'habilitacoes', 'habilitações', 'qualifications', 'certificates'],
  excecao_prepav:              ['excecao_prepav', 'prepav', 'excecao', 'exceção'],
  descricao_subida:            ['descricao_subida', 'descricao', 'descrição', 'observacoes', 'observações', 'notes'],
};

// Labels amigáveis para cada campo
export const FIELD_LABELS: Record<string, string> = {
  id: 'Nº Funcionário',
  nome: 'Nome',
  carreira: 'Carreira',
  sexo: 'Sexo',
  nivel_atual: 'Nível Atual',
  novo_nivel_2025: 'Novo Nível 2025',
  vencimento: 'Vencimento (€)',
  avaliacao: 'Avaliação',
  ultima_progressao: 'Última Progressão',
  data_entrada_funcao_publica: 'Entrada Função Pública',
  unidade_organica: 'Unidade Orgânica',
  subunidade_organica: 'Subunidade Orgânica',
  pontos_atuais: 'Pontos Atuais',
  email: 'Email',
  telefone: 'Telefone',
  data_nascimento: 'Data de Nascimento',
  cpf: 'CPF / NIF',
  endereco: 'Endereço',
  status: 'Estado',
  superior_hierarquico: 'Superior Hierárquico',
  categoria: 'Categoria',
  despacho_autorizacao: 'Despacho de Autorização',
  certificados: 'Certificados',
  excecao_prepav: 'Exceção PREPAV',
  descricao_subida: 'Descrição / Observações',
};

// ── Utilitários ────────────────────────────────────────────────────────────

export function stripBOM(text: string): string {
  return text.replace(/^\uFEFF/, '').replace(/^\u00EF\u00BB\u00BF/, '');
}

export function detectDelimiter(text: string): string {
  const lines = text.split('\n').slice(0, 3).filter((l) => l.trim());
  let semicolons = 0; let commas = 0; let tabs = 0;
  for (const line of lines) {
    semicolons += (line.match(/;/g) ?? []).length;
    commas += (line.match(/,/g) ?? []).length;
    tabs += (line.match(/\t/g) ?? []).length;
  }
  if (tabs > commas && tabs > semicolons) return '\t';
  if (semicolons > commas) return ';';
  return ',';
}

export function parseCSV(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  for (const line of lines) {
    if (!line.trim()) continue;
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === delimiter && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());
    rows.push(cells);
  }
  return rows;
}

export function normalizeHeader(h: string): string {
  return h
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export function findColumnIndex(headers: string[], aliases: string[]): number {
  const normalized = headers.map((h) => normalizeHeader(h));
  for (const alias of aliases) {
    const normAlias = normalizeHeader(alias);
    const idx = normalized.indexOf(normAlias);
    if (idx !== -1) return idx;
  }
  for (const alias of aliases) {
    const normAlias = normalizeHeader(alias);
    const idx = normalized.findIndex((h) => h.includes(normAlias) || normAlias.includes(h));
    if (idx !== -1) return idx;
  }
  return -1;
}

export function normalizeSexo(v: string): string {
  const u = v.trim().toUpperCase();
  if (u === 'M' || u === 'MASCULINO') return 'M';
  if (u === 'F' || u === 'FEMININO') return 'F';
  return 'M';
}

export function normalizeCarreira(v: string): string {
  const lower = v.trim().toLowerCase();
  if (lower.includes('técnica') || lower.includes('tecnica') || lower.includes('superior')) return 'Técnica Superior';
  if (lower.includes('técnico') || lower.includes('tecnico') || lower.includes('assistente t')) return 'Assistente Técnico';
  if (lower.includes('operacional')) return 'Assistente Operacional';
  return v.trim() || 'Técnica Superior';
}

export function normalizeAvaliacao(v: string): string {
  const lower = v.trim().toLowerCase();
  if (lower.includes('excelente')) return 'Excelente';
  if (lower.includes('muito') && lower.includes('bom')) return 'Muito Bom';
  if (lower.includes('adequado')) return 'Adequado';
  if (lower.includes('inadequado')) return 'Inadequado';
  if (lower.includes('bom')) return 'Bom';
  if (lower.includes('regular')) return 'Regular';
  if (lower.includes('relevante')) return 'Relevante';
  return 'Adequado';
}

export function normalizeDate(v: string): string | null {
  if (!v || v.trim() === '' || v.trim() === '—' || v.trim() === '-') return null;
  const s = v.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split('/');
    return `${y}-${m}-${d}`;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [d, m, y] = s.split('-');
    return `${y}-${m}-${d}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}

// Constrói um ParsedRow a partir de uma linha de dados e do mapeamento de colunas
export function buildParsedRow(
  row: string[],
  headers: string[],
  colIdx: Record<string, number>,
  rowIndex: number,
): ParsedRow {
  const get = (col: string): string => {
    const idx = colIdx[col];
    return idx !== -1 ? (row[idx] ?? '').trim() : '';
  };

  const raw: Record<string, string> = {};
  headers.forEach((h, idx) => { raw[h] = row[idx] ?? ''; });

  const errors: string[] = [];
  const idRaw = get('id');
  const nomeRaw = get('nome');
  const id = idRaw || `AUTO-${Date.now()}-${rowIndex}`;
  const nome = nomeRaw;
  if (!idRaw) errors.push('Nº de funcionário em falta — será gerado automaticamente');
  if (!nomeRaw) errors.push('Nome em falta — linha não pode ser importada sem nome');

  const nivelRaw = get('nivel_atual');
  const nivel_atual = nivelRaw ? parseInt(nivelRaw, 10) : 1;
  const novoNivelRaw = get('novo_nivel_2025');
  const novo_nivel_2025 = novoNivelRaw ? parseInt(novoNivelRaw, 10) : nivel_atual;
  const vencRaw = get('vencimento').replace(/[€\s]/g, '').replace(',', '.');
  const vencimento = vencRaw ? parseFloat(vencRaw) : 0;
  const pontosRaw = get('pontos_atuais').replace(',', '.');
  const pontos_atuais = pontosRaw ? parseFloat(pontosRaw) : 0;

  return {
    _raw: raw,
    _errors: errors,
    _rowIndex: rowIndex,
    id,
    nome,
    carreira: normalizeCarreira(get('carreira')),
    sexo: normalizeSexo(get('sexo')),
    nivel_atual: isNaN(nivel_atual) ? 1 : nivel_atual,
    novo_nivel_2025: isNaN(novo_nivel_2025) ? nivel_atual : novo_nivel_2025,
    vencimento: isNaN(vencimento) ? 0 : vencimento,
    avaliacao: normalizeAvaliacao(get('avaliacao')),
    ultima_progressao: normalizeDate(get('ultima_progressao')),
    data_entrada_funcao_publica: normalizeDate(get('data_entrada_funcao_publica')),
    unidade_organica: get('unidade_organica'),
    subunidade_organica: get('subunidade_organica'),
    pontos_atuais: isNaN(pontos_atuais) ? 0 : pontos_atuais,
    email: get('email') || null,
    telefone: get('telefone') || null,
    data_nascimento: normalizeDate(get('data_nascimento')),
    cpf: get('cpf') || null,
    endereco: get('endereco') || null,
    status: get('status') || null,
    superior_hierarquico: get('superior_hierarquico') || null,
    categoria: get('categoria') || null,
    despacho_autorizacao: get('despacho_autorizacao') || null,
    certificados: get('certificados') || null,
    excecao_prepav: ['true', '1', 'sim', 'yes'].includes(get('excecao_prepav').toLowerCase()),
    descricao_subida: get('descricao_subida') || null,
  };
}

// Constrói o mapeamento automático de colunas
export function buildAutoColIdx(headers: string[]): Record<string, number> {
  const colIdx: Record<string, number> = {};
  for (const [col, aliases] of Object.entries(COLUMN_MAP)) {
    colIdx[col] = findColumnIndex(headers, aliases);
  }
  return colIdx;
}
